import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { submitLead } from "@/lib/api";
import SectionLabel from "@/components/layout/SectionLabel";
import { Sparkles } from "lucide-react";

const INTERESTS = ["hot_water", "solar", "battery", "full_package"];

export default function AeroMax270() {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", region: "", interests: [],
  });
  const [submitting, setSubmitting] = useState(false);

  const toggleInterest = (key) =>
    setForm((s) => ({
      ...s,
      interests: s.interests.includes(key) ? s.interests.filter((k) => k !== key) : [...s.interests, key],
    }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) return toast.error("Name and email required");
    setSubmitting(true);
    try {
      await submitLead({
        type: "early_interest",
        name: form.name, email: form.email, phone: form.phone, region: form.region,
        notes: `Interests: ${form.interests.join(", ")}`,
      });
      toast.success("You're on the list. We'll keep you posted!");
      setForm({ name: "", email: "", phone: "", region: "", interests: [] });
    } catch { toast.error("Submission failed."); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="bg-white">
      <section className="relative overflow-hidden bg-[#0B132B] text-white">
        <div className="absolute inset-0 bg-grid opacity-[0.05]" />
        <div className="absolute -top-32 -right-32 h-[400px] w-[400px] rounded-full bg-[#10B981]/20 blur-3xl" />
        <div className="max-w-7xl mx-auto px-6 sm:px-8 py-24 relative">
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#10B981]/20 text-[#10B981] text-[11px] font-semibold tracking-widest uppercase">
            <Sparkles className="h-3 w-3" /> Coming soon
          </span>
          <h1 className="font-heading mt-5 text-4xl sm:text-6xl font-bold tracking-tight max-w-3xl">
            AeroMax 270L — Coming Soon
          </h1>
          <p className="mt-6 text-lg text-white/70 max-w-2xl">
            AeroMax Energy is working toward launching its own AeroMax-branded 270L all-in-one hot water heat pump for the New Zealand market. Part of our long-term vision to build a premium, affordable and reliable home electrification brand for Kiwi households.
          </p>
        </div>
      </section>

      <section className="py-20">
        <div className="max-w-2xl mx-auto px-6 sm:px-8">
          <Card data-testid="early-interest-form" className="rounded-3xl p-8 border-[#E2E8F0]">
            <SectionLabel>Early interest list</SectionLabel>
            <h2 className="font-heading mt-4 text-3xl font-bold text-[#0B132B]">Join the early interest list</h2>
            <form onSubmit={submit} className="mt-6 space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs">Name *</Label>
                  <Input data-testid="ei-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
                </div>
                <div>
                  <Label className="text-xs">Email *</Label>
                  <Input data-testid="ei-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
                </div>
                <div>
                  <Label className="text-xs">Phone</Label>
                  <Input data-testid="ei-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                </div>
                <div>
                  <Label className="text-xs">Region</Label>
                  <Input data-testid="ei-region" value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })} />
                </div>
              </div>
              <div>
                <Label className="text-xs">Interested in</Label>
                <div className="mt-2 grid grid-cols-2 gap-3">
                  {INTERESTS.map((k) => (
                    <label key={k} className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer ${form.interests.includes(k) ? "border-[#2563EB] bg-[#2563EB]/5" : "border-[#E2E8F0] bg-white"}`}>
                      <Checkbox checked={form.interests.includes(k)} onCheckedChange={() => toggleInterest(k)} />
                      <span className="text-sm font-medium capitalize">{k.replace("_", " ")}</span>
                    </label>
                  ))}
                </div>
              </div>
              <Button data-testid="ei-submit" type="submit" disabled={submitting} className="w-full rounded-full bg-[#0B132B] hover:bg-[#050914] text-white py-6 font-semibold">
                {submitting ? "Adding..." : "Join the AeroMax Early Interest List"}
              </Button>
              <p className="text-[10px] text-[#475569] text-center">
                Subject to compliance and product approval. No promises on launch dates.
              </p>
            </form>
          </Card>
        </div>
      </section>
    </div>
  );
}
